import React, {
    useState,
    useCallback,
    useEffect,
    ReactNode,
    useMemo,
    CSSProperties,
} from 'react';
import { styled } from '@mui/material';
import {
    TRACKABLE_EVENTS,
    trackEventBudget,
    trackEventPRH,
} from 'helpers/trackerHelpers';
import { BUDGET, HUMAN_RESOURCES, ModuleT } from 'business/constants';
import {
    FULL_SCREEN_Z_INDEX,
    FullScreenContext,
} from 'contexts/FullScreenContext';

type Props = {
    children: ReactNode | ReactNode[];
    // in Argument we need to disable the escape key because we rely
    // on the click on the button to trigger an event.
    disableEscape?: boolean;
    // for now we just need height to be setable for when the component is not full screen
    // if we add more CSS properties, we should consider using a full CSSProperties prop
    height?: CSSProperties['height'];
    module?: ModuleT | null;
    trackEventMetadata?: pendo.Metadata;
};

const StyDarkBackground = styled('div', { name: 'StyDarkBackground' })({
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'block',
    zIndex: FULL_SCREEN_Z_INDEX - 1,
});

const StyChildren = styled('div', {
    name: 'StyChildren',
    shouldForwardProp: (prop) => prop !== 'isFullScreen',
})<{ height: Props['height']; isFullScreen: boolean }>(({
    height,
    isFullScreen,
}) => {
    if (isFullScreen) {
        // This style enables making the modal content scrollable.
        const scrollableContent = {
            overflow: 'hidden',
            '& .MuiCardContent-root': {
                flexGrow: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            },
        };
        return {
            position: 'fixed',
            top: '20px',
            bottom: '20px',
            left: '20px',
            right: '20px',
            zIndex: FULL_SCREEN_Z_INDEX,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            ...scrollableContent,
        };
    }
    return {
        // because this is adding a div around the children, we need to be able set the height on it
        height,
    };
});

const FullScreenProvider = ({
    children,
    height = undefined,
    disableEscape = false,
    module = null,
    trackEventMetadata = undefined,
}: Props) => {
    const [isFullScreen, setIsFullScreen] = useState(false);

    const toggleIsFullScreen = useCallback(() => {
        setIsFullScreen((prevIsFullScreen: boolean) => !prevIsFullScreen);
        if (module === BUDGET) {
            const trackEventId = isFullScreen
                ? TRACKABLE_EVENTS.budget.general.fullScreen.close
                : TRACKABLE_EVENTS.budget.general.fullScreen.open;
            trackEventBudget(trackEventId);
        }
        if (module === HUMAN_RESOURCES) {
            const trackEventId = isFullScreen
                ? TRACKABLE_EVENTS.human_resources.fullScreen.close
                : TRACKABLE_EVENTS.human_resources.fullScreen.open;

            trackEventPRH(trackEventId, trackEventMetadata);
        }
    }, [isFullScreen]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (isFullScreen && event.key === 'Escape') {
                event.preventDefault();
                setIsFullScreen(false);
            }
        },
        [isFullScreen],
    );

    useEffect(() => {
        if (disableEscape) return;
        document.addEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line consistent-return
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [disableEscape, handleKeyDown]);

    const value = useMemo(
        () => ({ isFullScreen, toggleIsFullScreen }),
        [isFullScreen, toggleIsFullScreen],
    );

    return (
        <FullScreenContext.Provider value={value}>
            {isFullScreen && <StyDarkBackground />}
            <StyChildren isFullScreen={isFullScreen} height={height}>
                {children}
            </StyChildren>
        </FullScreenContext.Provider>
    );
};

export default FullScreenProvider;
